import { TERRAIN } from '../config';
import { simplex2 } from '../noise';

/**
 * Terrain height in cm at world (x, y). Port of ATerrainTile::GetTerrainHeightAtPoint
 * (P/TerrainTile.cpp:193-268, SPEC §2.2), seeded. Keeps the reference's quirks: the first octave
 * samples `y` instead of the rotated `uy` (A1), and the flatten/mountain switch at ridge 0.7.
 */
export function terrainHeight(seed: number, x: number, y: number): number {
  const S = simplex2(seed, 0);
  let output = 0;
  let denom = 0;
  let frequency = TERRAIN.frequency;
  let amplitude = TERRAIN.amplitude;
  let ridgeOutput = 0;
  let layerAngle = 0;
  const numLayers = 2;
  const f2 = 1.1;

  for (let i = 0; i < TERRAIN.octaves; i++) {
    const c = Math.cos(layerAngle);
    const s = Math.sin(layerAngle);
    const useX = x * c - y * s;
    const useY = x * s + y * c;
    const k3 = 0.00003 * frequency;
    const k2 = 0.00002 * frequency;

    output += (amplitude / numLayers) * (S(k3 * useX, k3 * y) * 0.5 + 0.5);

    const fbm1 = 2 * S(k3 * useX, k3 * useY);
    const fbm2 = 2 * S(k2 * useX, k2 * useY);
    const fbm3 = 4 * S(k3 * (useX + fbm1), k3 * (useY + fbm2));
    const fbm4 = 4 * S(k2 * (useX + fbm1), k2 * (useY + fbm2));
    const fbm5 = 10000 * S(k3 * (useX + fbm3), k3 * (useY + fbm4));
    const fbm6 = 10000 * S(k2 * (useX + fbm3), k2 * (useY + fbm4));
    output += (amplitude / numLayers) * (S(k3 * (useX + fbm5), k3 * (useY + fbm6)) * 0.5 + 0.5);

    ridgeOutput += amplitude * (1 - Math.abs(S(0.000005 * useX * frequency * f2 + 1257, 0.000005 * useY * frequency * f2 + 7834)));

    denom += amplitude;
    frequency *= TERRAIN.lacunarity;
    amplitude *= TERRAIN.persistence;
    layerAngle += 0.5;
  }

  const ridge = ridgeOutput / denom;
  if (ridge > TERRAIN.ridgeThreshold) output += (ridge - TERRAIN.ridgeThreshold) * TERRAIN.ridgeGain;
  else output *= TERRAIN.flatScale;

  return (output / denom) * TERRAIN.vertScale + TERRAIN.vertOffset;
}

/** Surface normal (unit, Z up) from central differences of the global height function. */
export function terrainNormal(seed: number, x: number, y: number, h: number): [number, number, number] {
  const dx = (terrainHeight(seed, x + h, y) - terrainHeight(seed, x - h, y)) / (2 * h);
  const dy = (terrainHeight(seed, x, y + h) - terrainHeight(seed, x, y - h)) / (2 * h);
  const len = Math.hypot(dx, dy, 1);
  return [-dx / len, -dy / len, 1 / len];
}


// ---------------------------------------------------------------------------------------------
// Graded city surface (our own addition, DECISIONS "Follow-up — Smoothing").
// The reference terrain has fine domain-warped bumps that make roads and sidewalks lumpy. Over flat
// (buildable) ground we blend the raw terrain toward a bilinear interpolation of a coarse lattice,
// weighted by the lattice slope. Everything is a function of global coordinates, so tiles agree.

/** Lattice spacing of the graded surface: the LOD2 grid step (15.625 m). */
export const GRADE_STEP = 1562.5;
/** Slopes at or below this are fully graded; at or above GRADE_SLOPE_HI they keep raw terrain. */
const GRADE_SLOPE_LO = 0.2;
const GRADE_SLOPE_HI = 0.4;

const latticeH = new Map<string, number>();
const latticeW = new Map<string, number>();

function lat(seed: number, i: number, j: number): number {
  const k = `${seed}|${i}|${j}`;
  let v = latticeH.get(k);
  if (v === undefined) {
    if (latticeH.size > 400000) latticeH.clear();
    v = terrainHeight(seed, i * GRADE_STEP, j * GRADE_STEP);
    latticeH.set(k, v);
  }
  return v;
}

function latWeight(seed: number, i: number, j: number): number {
  const k = `${seed}|${i}|${j}`;
  let v = latticeW.get(k);
  if (v === undefined) {
    if (latticeW.size > 400000) latticeW.clear();
    const z = lat(seed, i, j);
    const s = Math.max(Math.abs(lat(seed, i + 1, j) - z), Math.abs(lat(seed, i - 1, j) - z), Math.abs(lat(seed, i, j + 1) - z), Math.abs(lat(seed, i, j - 1) - z)) / GRADE_STEP;
    v = Math.max(0, Math.min(1, (GRADE_SLOPE_HI - s) / (GRADE_SLOPE_HI - GRADE_SLOPE_LO)));
    latticeW.set(k, v);
  }
  return v;
}

/** Bilinear graded height and blend weight at (x, y). */
export function gradedHeight(seed: number, x: number, y: number): { h: number; w: number } {
  const fx = x / GRADE_STEP;
  const fy = y / GRADE_STEP;
  const i = Math.floor(fx);
  const j = Math.floor(fy);
  const u = fx - i;
  const v = fy - j;
  const b = (f: (a: number, c: number) => number) => (f(i, j) * (1 - u) + f(i + 1, j) * u) * (1 - v) + (f(i, j + 1) * (1 - u) + f(i + 1, j + 1) * u) * v;
  return { h: b((a, c) => lat(seed, a, c)), w: b((a, c) => latWeight(seed, a, c)) };
}

/**
 * The ground everything stands on: raw terrain on steep ground, the smooth graded surface on flat
 * ground, blended in between. Used by the terrain mesh and by every draped city surface.
 */
export function surfaceHeight(seed: number, x: number, y: number): number {
  const g = gradedHeight(seed, x, y);
  if (g.w >= 1) return g.h;
  const raw = terrainHeight(seed, x, y);
  return raw + (g.h - raw) * g.w;
}
